import {
  FeatureFlagsContext,
  messageWithOrWithoutCount,
  SelectionProviderChildProps,
  SiteUrlsContext
} from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import * as React from 'react';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';

import { Messages } from '../../../gen-src/Localize';
import { DatasourceQuery_publishedDatasources } from '../../../gen-src/queries/DatasourceQuery';
import { BrowserTitleUpdater, BrowserTitleUpdateType } from '../BrowserTitleUpdater';
import { FieldDetailsDialog } from '../FieldDetails/FieldDetailsDialog';
import { ErrorListProvider } from '../error/components/ErrorListProvider';
import { ErrorToasts } from '../error/components/ErrorToasts';
import { UnrecognizedUrl } from '../error/components/UnrecognizedUrl';
import { LineageItemType } from '../lineage/LineageItemType';
import {
  getAnchorCountFromConfig,
  LineageItemConfig,
  lineageItemKey,
  LineageSection,
  LineageSectionType,
} from '../placePage/LineageConfiguration';
import { DatasourceLineageSubplaceLayout } from '../placePage/LineageSubplaceLayout';
import { DatasourceLineagePaneParams, memoizedGetDatasourceLineageConfiguration } from './DatasourceLineageConfiguration';
import { DatasourceLineageCounts, DatasourceLineageCountsQueryWrapper } from './DatasourceLineageCountsQueryWrapper';
import { DatasourceQueryWrapperWithContext } from './DatasourceQueryWrapper';
import { DatasourceSubplaceParamsProvider } from './DatasourceSubplaceParamsProvider';


export interface DatasourceLineageSubplaceRouterProps {
  readonly vizportalUrlId: string;
}

export interface DatasourceLineageSubplaceRouterState {
  readonly hackyUpdateNumber: number; // Increment number to rerender
}

export class DatasourceLineageSubplaceRouter extends React.Component<
  DatasourceLineageSubplaceRouterProps,
  DatasourceLineageSubplaceRouterState
  > {

  private localIsMounted = false;

  public constructor(props: DatasourceLineageSubplaceRouterProps) {
    super(props);
    this.state = {
      hackyUpdateNumber: 0
    };
  }

  public componentDidMount() {
    this.localIsMounted = true;
  }

  public componentWillUnmount() {
    this.localIsMounted = false;
  }

  private rerenderDatasourceLineageSubplaceRouter = () => {
    if (this.localIsMounted) {
      this.setState({
        hackyUpdateNumber: this.state.hackyUpdateNumber + 1
      });
    }
  }

  public render(): JSX.Element {
    return (
      <SiteUrlsContext.Consumer>
        {urls =>
          <Route
            path={urls.datasourceLineagePath(this.props.vizportalUrlId)}
            render={() => this.renderDatasourceLineageRoute(urls)}
          />
        }
      </SiteUrlsContext.Consumer>
    );
  }

  private readonly renderDatasourceLineageRoute = (urls: SiteUrls): JSX.Element => {
    return (
      <FeatureFlagsContext.Consumer>
        {
          featureFlags =>
            <Switch>
              {memoizedGetDatasourceLineageConfiguration(featureFlags.metricsDataCatalogEnabled, featureFlags.publishedConnectionsLineageEnabled).map(lineageSection =>
                lineageSection.items.map((lineageItem, i) =>
                  <Route
                    key={i}
                    path={lineageItem.path(urls, this.props.vizportalUrlId)}
                    render={() => this.renderLineageItemRoute(urls, lineageItem, lineageSection)}
                  />
                )
              )}
              <Redirect
                exact={true}
                from={urls.datasourceLineagePath(this.props.vizportalUrlId)}
                to={urls.datasourceLineageFieldsPath(this.props.vizportalUrlId)}
              />
              <UnrecognizedUrl
                backToButtonLabel={Messages.BackToFields()}
                backToButtonUrl={urls.datasourceLineageFieldsPath(this.props.vizportalUrlId)}
              />
            </Switch>
        }
      </FeatureFlagsContext.Consumer>
    );
  }

  private readonly renderLineageItemRoute = (
    urls: SiteUrls,
    lineageItem: LineageItemConfig<DatasourceLineageCounts, DatasourceLineagePaneParams>,
    lineageSection: LineageSection<DatasourceLineageCounts, DatasourceLineagePaneParams>,
  ): JSX.Element => {
    return (
      <DatasourceSubplaceParamsProvider>
        {params =>
          <ErrorListProvider>
            <ErrorToasts />
            <DatasourceQueryWrapperWithContext vizportalUrlId={this.props.vizportalUrlId}>
              {(datasource: DatasourceQuery_publishedDatasources, canSetDescription: boolean) =>
                <DatasourceLineageCountsQueryWrapper
                  vizportalUrlId={this.props.vizportalUrlId}
                  selectedFieldKeys={params.selectedFields}
                >
                  {({ countsLoading, unfilteredCounts, filteredCounts }) =>
                    <>
                      <DatasourceLineageSubplace
                        vizportalUrlId={this.props.vizportalUrlId}
                        datasource={datasource}
                        lineageItem={lineageItem}
                        lineageSection={lineageSection}
                        countsLoading={countsLoading}
                        unfilteredCounts={unfilteredCounts}
                        filteredCounts={filteredCounts}
                        urlSearchValue={params.urlSearchValue}
                        clearFieldSelection={params.clearFieldSelection}
                        selectedKeys={params.selectedFields}
                        updateSelectionForKey={params.updateFieldSelection}
                        hackyUpdateNumber={this.state.hackyUpdateNumber}
                      />
                      {lineageSection.sectionType === LineageSectionType.Anchor ?
                        <Route
                          path={urls.datasourceLineageFieldPath(this.props.vizportalUrlId, ':fieldId')}
                          render={({ history, location, match }: RouteComponentProps<{ fieldId: string; }>) =>
                            <FeatureFlagsContext.Consumer>
                              {featureFlags =>
                                <FieldDetailsDialog
                                  fieldId={match.params.fieldId}
                                  makeFieldUrl={(fieldId) => urls.datasourceLineageFieldUrl(this.props.vizportalUrlId, fieldId)}
                                  onClose={() => history.replace({
                                    ...location,
                                    pathname: urls.datasourceLineageFieldsPath(this.props.vizportalUrlId)
                                  })}
                                  afterDescriptionChange={this.rerenderDatasourceLineageSubplaceRouter}
                                  canEditDescription={
                                    featureFlags.writableFieldDescriptionsEnabled && canSetDescription
                                  }
                                  datasourceLuid={datasource.luid}
                                />
                              }
                            </FeatureFlagsContext.Consumer>
                          }
                        />
                        : null
                      }
                    </>
                  }
                </DatasourceLineageCountsQueryWrapper>
              }
            </DatasourceQueryWrapperWithContext>
          </ErrorListProvider>
        }
      </DatasourceSubplaceParamsProvider>
    );
  }

}


type DatasourceLineageSubplaceProps = {
  readonly vizportalUrlId: string;
  readonly datasource: DatasourceQuery_publishedDatasources;
  readonly lineageItem: LineageItemConfig<DatasourceLineageCounts, DatasourceLineagePaneParams>;
  readonly lineageSection: LineageSection<DatasourceLineageCounts, DatasourceLineagePaneParams>;
  readonly countsLoading: boolean;
  readonly unfilteredCounts: DatasourceLineageCounts | null;
  readonly filteredCounts: DatasourceLineageCounts | null;
  readonly urlSearchValue: string;
  readonly clearFieldSelection: () => void;
  readonly hackyUpdateNumber: number;
} & SelectionProviderChildProps;

class DatasourceLineageSubplace extends React.PureComponent<DatasourceLineageSubplaceProps> {

  public render(): JSX.Element {
    const selectedFields = Object.keys(this.props.selectedKeys);
    const hasFilter = selectedFields.length > 0;
    const filteredFieldId: string | null = hasFilter ? selectedFields[0] : null;
    const lineageCounts = this.props.filteredCounts ? this.props.filteredCounts : this.props.unfilteredCounts;
    return (
      <>
        <BrowserTitleUpdater
          name={this.props.datasource.name}
          browserTitleUpdateType={BrowserTitleUpdateType.TITLE_WITH_ENTITY_NAME_AND_SUBSPACE_NAME}
          subspaceName={Messages.LineageNavHeader()}
        />
        <FeatureFlagsContext.Consumer>
          {
            featureFlags =>
              <DatasourceLineageSubplaceLayout
                activeLineageItemKey={lineageItemKey(this.props)}
                selectedAnchorItemIds={selectedFields}
                pane={this.props.lineageItem.paneFactory({
                  title: messageWithOrWithoutCount(
                    lineageCounts && this.props.lineageItem.count(lineageCounts),
                    this.props.lineageItem.messagesForPane
                  ),
                  vizportalUrlId: this.props.vizportalUrlId,
                  filteredFieldId,
                  count: lineageCounts && this.props.lineageItem.count(lineageCounts),
                  selectedKeys: this.props.selectedKeys,
                  updateSelectionForKey: this.props.updateSelectionForKey,
                  hackyUpdateNumber: this.props.hackyUpdateNumber
                })}
                lineageSectionType={this.props.lineageSection.sectionType}
                vizportalUrlId={this.props.vizportalUrlId}
                countsLoading={this.props.countsLoading}
                unfilteredCounts={this.props.unfilteredCounts}
                filteredCounts={this.props.filteredCounts}
                urlSearchValue={this.props.urlSearchValue}
                clearFieldSelection={this.props.clearFieldSelection}
                anchorName={this.props.datasource.name}
                anchorCount={getAnchorCountFromConfig(
                  memoizedGetDatasourceLineageConfiguration(featureFlags.metricsDataCatalogEnabled, featureFlags.publishedConnectionsLineageEnabled),
                  this.props.unfilteredCounts)}
                filteredOnItemType={hasFilter ? LineageItemType.Datasource : null}
                filteredItemName={this.props.filteredCounts?.filteredFieldName ?? null}
              />
          }
        </FeatureFlagsContext.Consumer>
      </>
    );
  }

}
